import { useMemo } from "react";
import { GridMode } from "../interfaces/ui";
import { useSettings } from "./settings";

export interface GridConfig {
  gridMode: GridMode;
  pageSize: number | "auto";
  autoTileMinPx: number;
}

const DEFAULT_AUTO_TILE_MIN = 180;

/** A page size (>0) means fixed pagination; unset (or 0) means automatic. */
const coercePageSize = (raw: unknown): number | "auto" =>
  typeof raw === "number" && Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : "auto";

/**
 * Resolve the grid layout configuration from user settings, applying defaults
 * (adaptive / auto / 180). Returns props ready to spread into
 * `<NewTriLayoutContainer>`.
 */
export const useGridConfig = (): GridConfig => {
  const [settings] = useSettings();
  const grid = settings.ui?.grid;
  return useMemo(() => {
    const gridMode = grid?.mode === GridMode.Template ? GridMode.Template : GridMode.Adaptive;
    const pageSize = coercePageSize(grid?.pageSize);
    const autoTileMinPx = typeof grid?.autoTileMinPx === "number" && grid.autoTileMinPx > 0
      ? grid.autoTileMinPx
      : DEFAULT_AUTO_TILE_MIN;
    return { gridMode, pageSize, autoTileMinPx };
  }, [grid?.mode, grid?.pageSize, grid?.autoTileMinPx]);
};
